import { useCallback } from 'react';

import { BigNumber } from 'ethers';
import { useRouter } from 'next/router';
import toast from 'react-hot-toast';
import { useAccount } from 'wagmi';

import { acceptLoanRequestByBackend } from '@/api/loans';
import { IAcceptLoanRequestPayload } from '@/api/loans/model';
import { ILoanOpenOffer } from '@/redux/loans/model';
import { checkERC20Balance } from '@/utils/checkERC20Balance';
import { convertValueToWei } from '@/utils/convertValueToWei';
import { getContractDecimals } from '@/utils/getContractDecimals';
import { unfishedOperations } from 'constant';
import { useErc20Contract, useLoanManagerContract, useNettyContract } from 'smart-contracts/contracts';
import { AddressType, ITransaction } from 'types';

import { useApproveMiddleware } from './useApproveMiddleware';

const useAcceptCounterOffer = () => {
  const nettyContract = useNettyContract();
  const router = useRouter();
  const { address } = useAccount();
  const loanManagerContract = useLoanManagerContract();
  const { approveNFTMiddleware } = useApproveMiddleware();
  const generateERC20Contract = useErc20Contract();

  const acceptCounterOffer = useCallback(
    async (offer: ILoanOpenOffer, tokenId: string, ownerAddress: AddressType, nftImage?: string) => {
      try {
        const ERC20Contract = generateERC20Contract(offer.currency);
        if (!ERC20Contract) return;
        const decimals = await getContractDecimals(ERC20Contract);
        const loanAmount = BigNumber.from(convertValueToWei(offer.principal, decimals));
        const lenderAddress = offer.lenderAddress || '0x2DC67345a60b5f2BA1d4f4bB661F6Ec31AF6B061';
        if (!(await checkERC20Balance(ERC20Contract, lenderAddress, loanAmount, "Lender haven't enough funds now."))) return;
        const isCollection = !!offer?.isCollection;
        const apr = offer.apr * 100;
        let waiting;
        let offerType: string;
        const loanRequestByBackendPayload: Omit<IAcceptLoanRequestPayload, 'loanChainId' | 'transaction'> = {
          lenderAddress,
          requestId: offer.id,
          tokenId,
          image: nftImage,
          collectionAddress: offer.collectionAddress,
        };

        await approveNFTMiddleware(offer.collectionAddress, tokenId);

        if (!isCollection) {
          const payload = [
            [offer.tokenId],
            offer.collectionAddress,
            offer.currency,
            lenderAddress,
            ownerAddress,
            loanAmount, // principal
            apr,
            offer.termUnix,
            offer.nonce,
          ];
          waiting = await nettyContract.functions.acceptLoanOffer(offer.signature, payload);
          offerType = 'Counter';
        } else {
          const payload = [
            offer.collectionId, // collectionAddress,
            offer.currency,
            lenderAddress,
            loanAmount,
            apr,
            offer.termUnix,
            offer.nonce,
          ];

          waiting = await nettyContract.functions.acceptLoanCollectionOffer(offer.signature, payload, [tokenId]);
          offerType = 'Collection';
          loanRequestByBackendPayload.tokenId = tokenId;
          loanRequestByBackendPayload.borrowerAddress = address;
          loanRequestByBackendPayload.collectionMarketplaceId = offer.collection.address;
        }

        await toast.promise(
          (async () => {
            const { transactionHash: transaction }: ITransaction = await waiting.wait();
            const [loanDetails, loanChainId] = await loanManagerContract.functions.getLoan(offer.collectionAddress, [tokenId], ownerAddress, offer.nonce);
            const requestPayload = { ...loanRequestByBackendPayload, loanChainId: loanChainId.toString(), transaction, tokenId, lenderReceiptId: parseInt(loanDetails.lenderReceiptId._hex, 16), borrowerReceiptId: parseInt(loanDetails.borrowerReceiptId._hex, 16) };
            localStorage.setItem(unfishedOperations.ACCEPT_COUNTER_OFFER, JSON.stringify(requestPayload));
            await acceptLoanRequestByBackend(requestPayload);
            localStorage.removeItem(unfishedOperations.ACCEPT_COUNTER_OFFER);
          })(),
          {
            error: 'Something went wrong',
            loading: 'Please wait until your transaction is finished.',
            success: `The ${offerType} offer has been successfully accepted.`,
          }
        );

        router.push('/loans/active');
      } catch (e: any) {
        if (e.code !== 'ACTION_REJECTED') {
          toast.error(e.message);
        }

        throw e;
      }
    },
    [generateERC20Contract, approveNFTMiddleware, router, nettyContract.functions, address, loanManagerContract.functions]
  );

  return { acceptCounterOffer };
};

export { useAcceptCounterOffer };
